import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { verifySubscription } from "../utils/subscriptionHelper";
import { isValidIndianMobile, formatIndianMobile } from "../utils/phoneUtils";

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, isLoading: userLoading, isAuthenticated } = useAuth();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    "razorpay_subscription"
  );
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoEntryStatus, setAutoEntryStatus] = useState("checking");
  const [autoEntryAttempts, setAutoEntryAttempts] = useState(0);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState("");
  const [validationError, setValidationError] = useState("");

  // Razorpay Subscription Integration
  // You need a backend endpoint to create a subscription using your key_id/key_secret
  // Example: POST /api/create-subscription { plan_id, customer details }
  // Validate and format the mobile number for subscription
  const getFormattedMobileNumber = () => {
    if (!mobileNumber) {
      throw new Error("Mobile number is required");
    }

    if (!isValidIndianMobile(mobileNumber)) {
      throw new Error(
        "Please enter a valid Indian mobile number (10 digits, starting with 6-9)"
      );
    }

    return formatIndianMobile(mobileNumber);
  };

  const handleRazorpaySubscription = async () => {
    setLoading(true);
    setValidationError("");
    try {
      // Basic validation
      if (!email) {
        throw new Error("Email is required");
      }

      // Get formatted mobile number
      const formattedContact = getFormattedMobileNumber();

      console.log("Creating subscription for:", {
        email,
        contact: formattedContact,
        name: getNameFromEmail(email),
      });

      const API_BASE_URL = window.location.origin;
      console.log("Using API base URL:", API_BASE_URL);

      // First test if server is accessible
      try {
        const testResponse = await fetch(`${API_BASE_URL}/api/test`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!testResponse.ok) {
          const errorData = await testResponse.text();
          console.error("Server test failed:", errorData);
          throw new Error("Server is not responding properly");
        }

        console.log("Server test successful");
      } catch (error) {
        console.error("Server connection test failed:", error);
        throw new Error(
          "Cannot connect to payment server. Please try again later."
        );
      }

      // Proceed with subscription creation
      const response = await fetch(`${API_BASE_URL}/api/create-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        mode: "cors",
        body: JSON.stringify({
          email,
          contact: formattedContact,
          name: getNameFromEmail(email),
        }),
      });

      // First check if response is ok
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.details || errorData.error || "Subscription creation failed"
        );
      }

      // Then try to parse the JSON response
      const data = await response.json();
      if (!data || !data.subscription_id) {
        throw new Error(
          "Invalid response from server - missing subscription ID"
        );
      }
      // Load Razorpay script if not already loaded
      try {
        if (!window.Razorpay) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.async = true;
            script.onload = resolve;
            script.onerror = () =>
              reject(new Error("Failed to load Razorpay script"));
            document.body.appendChild(script);
          });
        }

        // Ensure Razorpay is loaded
        if (!window.Razorpay) {
          throw new Error("Razorpay failed to initialize");
        }
      } catch (error) {
        console.error("Razorpay script loading error:", error);
        throw new Error("Failed to load payment system. Please try again.");
      }

      // Open Razorpay Checkout for subscription using the already validated number
      const options = {
        key: "rzp_live_RNpOLtuSvQp2tK", // Your Razorpay Key ID
        subscription_id: data.subscription_id,
        name: "FOZZIL Intelligence Subscription",
        description: "Monthly Plan",
        image: "/logo192.png",
        handler: function (response) {
          // Handle successful payment
          updateSubscriptionStatus({
            payment_id: response.razorpay_payment_id,
            subscription_id: response.razorpay_subscription_id,
            signature: response.razorpay_signature,
            method: "razorpay_subscription",
          }).then(() => {
            // After successful update, redirect to FOZZIL Intelligence
            redirectToIntelligence(true);
          });
        },
        prefill: {
          email,
          contact: formattedContact,
          name: getNameFromEmail(email),
        },
        theme: { color: "#3399cc" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
      setLoading(false);
    } catch (err) {
      setValidationError("Error initiating subscription: " + err.message);
      setLoading(false);
    }
  };

  // Auto-fill email from authenticated user (login)
  useEffect(() => {
    if (!userLoading && isAuthenticated && user?.email) {
      setEmail(user.email);
      setAutoEntryStatus("found");
      console.log(
        "✅ Auto-fill: populated email from logged-in user:",
        user.email
      );
    }
  }, [user, isAuthenticated, userLoading]);

  const planPrice = 1.0;
  const convenienceFee = 2.2;
  const totalAmount = planPrice + convenienceFee;

  // Helper function to recursively find email in any object structure
  const findEmailInObject = (obj) => {
    if (!obj) return null;

    // If it's a string and looks like an email
    if (typeof obj === "string" && obj.includes("@") && obj.includes(".")) {
      const emailMatch = obj.match(
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/
      );
      return emailMatch ? emailMatch[0] : null;
    }

    // If it's an object, search all its properties
    if (typeof obj === "object" && obj !== null) {
      // Check common email property names first
      const emailProps = [
        "email",
        "user_email",
        "userEmail",
        "mail",
        "emailAddress",
        "user_metadata",
      ];
      for (const prop of emailProps) {
        if (
          obj[prop] &&
          typeof obj[prop] === "string" &&
          obj[prop].includes("@")
        ) {
          return obj[prop];
        }
      }

      // Check nested user object
      if (obj.user) {
        const userEmail = findEmailInObject(obj.user);
        if (userEmail) return userEmail;
      }

      // Check all other properties
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const result = findEmailInObject(obj[key]);
          if (result) return result;
        }
      }
    }

    return null;
  };

  // Enhanced postMessage listener for FOZZIL Intelligence communication
  useEffect(() => {
    const handleMessage = (event) => {
      console.log("📨 Received postMessage:", event.origin, event.data);

      // Accept messages from FOZZIL Intelligence domains (multiple possible domains)
      const allowedOrigins = [
        "https://intelligence.fozzil.in",
        "https://fozzil.in",
        "http://localhost:3000",
        "http://127.0.0.1:3000",
      ];

      if (!allowedOrigins.includes(event.origin)) {
        console.log("❌ Message from unauthorized origin:", event.origin);
        return;
      }

      // Handle different message types
      if (event.data.type === "FOZZIL_USER_DATA" && event.data.user) {
        const userData = event.data.user;
        const userEmail = userData.email || userData.user_metadata?.email;
        if (userEmail) {
          setEmail(userEmail);
          setAutoEntryStatus("found");
          console.log(
            "✅ Auto-entry SUCCESS: Received user data from FOZZIL Intelligence:",
            userEmail
          );
        }
      }

      // Handle direct user info
      else if (event.data.email) {
        setEmail(event.data.email);
        setAutoEntryStatus("found");
        console.log(
          "✅ Auto-entry SUCCESS: Received direct email:",
          event.data.email
        );
      }

      // Handle auth response
      else if (
        event.data.type === "AUTH_RESPONSE" &&
        event.data.authenticated
      ) {
        if (event.data.user?.email) {
          setEmail(event.data.user.email);
          setAutoEntryStatus("found");
          console.log(
            "✅ Auto-entry SUCCESS: Received auth response with email:",
            event.data.user.email
          );
        }
      }

      // Handle session data
      else if (event.data.type === "SESSION_DATA") {
        const sessionEmail =
          event.data.session?.user?.email || event.data.user?.email;
        if (sessionEmail) {
          setEmail(sessionEmail);
          setAutoEntryStatus("found");
          console.log(
            "✅ Auto-entry SUCCESS: Received session data with email:",
            sessionEmail
          );
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Also request user data on load if we're in an iframe or opened by another window
    if (window.opener || window !== window.top) {
      console.log("🔍 Requesting user data from FI website parent/opener...");
      const target = window.opener || window.parent;

      // Send multiple request types to increase chances of success
      const requestTypes = [
        "REQUEST_USER_DATA",
        "REQUEST_FOZZIL_USER_DATA",
        "GET_USER_EMAIL",
        "GET_CURRENT_USER",
      ];

      requestTypes.forEach((type) => {
        target.postMessage(
          {
            type: type,
            source: "fozzil_payment_page",
            timestamp: Date.now(),
          },
          "*"
        );
      });
    }

    // Also try to communicate with FI website if opened in new tab
    setTimeout(() => {
      if (window.opener) {
        console.log("🔍 Secondary request to FI website opener...");
        window.opener.postMessage(
          {
            type: "PAYMENT_PAGE_READY",
            source: "fozzil_payment",
            needsUserData: true,
          },
          "*"
        );
      }
    }, 1000);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Helper function to extract exact name from email
  const getNameFromEmail = (emailAddress) => {
    if (!emailAddress || !emailAddress.includes("@")) return "FOZZIL User";

    // Extract username part before @
    const username = emailAddress.split("@")[0];

    // Handle different email formats:
    // john.doe@domain.com -> John Doe
    // johnsmith123@domain.com -> Johnsmith
    // j.smith@domain.com -> J Smith
    // first_last@domain.com -> First Last

    let cleanName = username;

    // Replace dots and underscores with spaces
    cleanName = cleanName.replace(/[._]/g, " ");

    // Remove numbers but keep the word structure
    cleanName = cleanName.replace(/\d+/g, "");

    // Remove extra spaces and trim
    cleanName = cleanName.replace(/\s+/g, " ").trim();

    // If no spaces found, try to split camelCase
    if (!cleanName.includes(" ") && cleanName.length > 1) {
      // Split camelCase: johnSmith -> john Smith
      cleanName = cleanName.replace(/([a-z])([A-Z])/g, "$1 $2");
    }

    // Capitalize first letter of each word and make rest lowercase
    const formattedName = cleanName
      .split(" ")
      .filter((word) => word.length > 0) // Remove empty strings
      .map((word) => {
        if (word.length === 1) {
          return word.toUpperCase(); // Single letters remain uppercase (like "J" in "J Smith")
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");

    // Return formatted name or fallback
    const finalName = formattedName.length > 0 ? formattedName : "FOZZIL User";

    console.log(`📧 Name extraction: ${emailAddress} → ${finalName}`);

    return finalName;
  };

  // Enhanced auto-entry: Check authentication and pre-fill user data from FOZZIL Intelligence
  useEffect(() => {
    const checkFozzilIntelligenceAuth = async () => {
      try {
        console.log("🔍 Starting auto-entry detection from FI website...");

        // Method 1: URL parameters (highest priority - direct from FOZZIL Intelligence)
        const urlParams = new URLSearchParams(window.location.search);
        const userEmail =
          urlParams.get("user_email") ||
          urlParams.get("email") ||
          urlParams.get("user-email");
        const userId =
          urlParams.get("user_id") ||
          urlParams.get("id") ||
          urlParams.get("user-id");

        if (userEmail && userEmail.includes("@")) {
          setEmail(userEmail);
          setAutoEntryStatus("found");
          console.log(
            "✅ Auto-entry SUCCESS: Got email from FI website URL:",
            userEmail
          );
          console.log("✅ User ID from FI website URL:", userId);
          return;
        }

        // Method 1.5: Check window.name for passed data (some sites use this)
        if (window.name) {
          try {
            const nameData = JSON.parse(window.name);
            if (nameData.email && nameData.email.includes("@")) {
              setEmail(nameData.email);
              setAutoEntryStatus("found");
              console.log(
                "✅ Auto-entry SUCCESS: Got email from window.name:",
                nameData.email
              );
              return;
            }
          } catch (e) {
            // window.name is not JSON, ignore
          }
        }

        // Method 2: Supabase authentication (if available)
        if (!userLoading && isAuthenticated && user) {
          setEmail(user.email || "");
          setAutoEntryStatus("found");
          console.log(
            "✅ Auto-entry SUCCESS: FOZZIL Intelligence User ID:",
            user.id
          );
          console.log(
            "✅ Auto-entry SUCCESS: FOZZIL Intelligence User Email:",
            user.email
          );
          return;
        }

        // Method 3: Multiple localStorage keys (FOZZIL Intelligence might use different keys)
        const possibleKeys = [
          "fozzil-user",
          "fozzil_user",
          "fi-user",
          "fi_user",
          "intelligence-user",
          "supabase.auth.token",
          "sb-zpytlewkvsxnqhjcqebk-auth-token",
          "fozzil_user_data",
          "user_session",
          "auth_user",
          "currentUser",
          "user_data",
          "session_data",
        ];

        for (const key of possibleKeys) {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            try {
              const userData = JSON.parse(storedData);

              // Check different possible structures
              const email =
                userData.email ||
                userData.user?.email ||
                userData.user_metadata?.email ||
                userData.identities?.[0]?.identity_data?.email;

              if (email) {
                setEmail(email);
                setAutoEntryStatus("found");
                console.log(
                  `✅ Auto-entry SUCCESS: Found user in localStorage (${key}):`,
                  email
                );
                return;
              }
            } catch (e) {
              // If it's a string token, try to decode it
              if (
                typeof storedData === "string" &&
                storedData.includes("user")
              ) {
                console.log(
                  `🔍 Found auth token in ${key}, but couldn't parse user data`
                );
              }
            }
          }
        }

        // Method 3.5: Scan all localStorage for any email-like data (aggressive search)
        console.log("🔍 Scanning all localStorage for FI email data...");
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            (key.includes("user") ||
              key.includes("auth") ||
              key.includes("session"))
          ) {
            try {
              const data = localStorage.getItem(key);
              if (data && data.includes("@") && data.includes(".")) {
                // Try to parse as JSON first
                try {
                  const parsed = JSON.parse(data);
                  const foundEmail = findEmailInObject(parsed);
                  if (foundEmail) {
                    setEmail(foundEmail);
                    setAutoEntryStatus("found");
                    console.log(
                      `✅ Auto-entry SUCCESS: Found email in localStorage scan (${key}):`,
                      foundEmail
                    );
                    return;
                  }
                } catch (e) {
                  // If not JSON, check if the raw data looks like an email
                  const emailMatch = data.match(
                    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/
                  );
                  if (emailMatch) {
                    setEmail(emailMatch[0]);
                    setAutoEntryStatus("found");
                    console.log(
                      `✅ Auto-entry SUCCESS: Found email pattern in localStorage (${key}):`,
                      emailMatch[0]
                    );
                    return;
                  }
                }
              }
            } catch (e) {
              // Skip this key if there's an error
            }
          }
        }

        // Method 4: Try to fetch from Supabase session directly
        try {
          const { supabase } = await import("../supabaseClient");
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();

          if (!error && session?.user?.email) {
            setEmail(session.user.email);
            setAutoEntryStatus("found");
            console.log(
              "✅ Auto-entry SUCCESS: Got email from Supabase session:",
              session.user.email
            );
            return;
          }
        } catch (supabaseError) {
          console.log(
            "⚠️ Supabase not available or error:",
            supabaseError.message
          );
        }

        // Method 5: Check sessionStorage as backup
        const sessionKeys = ["fozzil-user", "user_data", "auth_session"];
        for (const key of sessionKeys) {
          const sessionData = sessionStorage.getItem(key);
          if (sessionData) {
            try {
              const userData = JSON.parse(sessionData);
              const email = userData.email || userData.user?.email;
              if (email) {
                setEmail(email);
                setAutoEntryStatus("found");
                console.log(
                  `✅ Auto-entry SUCCESS: Found user in sessionStorage (${key}):`,
                  email
                );
                return;
              }
            } catch (e) {
              console.log(`⚠️ Could not parse sessionStorage ${key}`);
            }
          }
        }

        // Method 6: Check if we're in an iframe and request parent data
        if (window !== window.top) {
          console.log(
            "🔍 Detected iframe, requesting user data from parent..."
          );
          window.parent.postMessage(
            {
              type: "REQUEST_FOZZIL_USER_DATA",
              source: "payment_page",
            },
            "*"
          );

          // Wait a bit for response before giving up
          setTimeout(() => {
            if (!email) {
              console.log("⚠️ No response from parent iframe");
            }
          }, 2000);
        }

        console.log("❌ Auto-entry: No user data found with any method");
        console.log("🔄 Providing manual input option...");
        setAutoEntryStatus("not_found");
      } catch (error) {
        console.error("❌ Error in auto-entry detection:", error);
        setAutoEntryStatus("not_found");
      }
    };

    // Run auto-entry detection
    checkFozzilIntelligenceAuth();

    // Enhanced retry mechanism for FI website integration
    const retryTimer = setTimeout(() => {
      if (autoEntryStatus === "checking" && autoEntryAttempts < 5) {
        setAutoEntryAttempts((prev) => prev + 1);
        console.log(
          `🔄 FI Auto-entry retry attempt ${autoEntryAttempts + 1}/5`
        );
        checkFozzilIntelligenceAuth();

        // Also try requesting from parent again on retry
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "REQUEST_USER_DATA_RETRY",
              attempt: autoEntryAttempts + 1,
              source: "fozzil_payment_page",
            },
            "*"
          );
        }
      }
    }, 1500); // Shorter retry interval for better UX

    // Also run when userLoading completes
    if (!userLoading) {
      setTimeout(checkFozzilIntelligenceAuth, 100);
    }

    return () => clearTimeout(retryTimer);
  }, [user, isAuthenticated, userLoading, autoEntryAttempts, autoEntryStatus]);

  // Function to update subscription status in intelligence.fozzil.in
  const updateSubscriptionStatus = async (paymentData) => {
    try {
      console.log("🔄 Updating subscription status in FOZZIL Intelligence...");

      // Method 1: Direct API call to intelligence.fozzil.in
      try {
        const response = await fetch(
          "https://intelligence.fozzil.in/api/update-subscription",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: paymentData.email,
              user_id: paymentData.user_id,
              payment_id: paymentData.payment_id,
              amount: paymentData.amount,
              plan: paymentData.plan,
              status: paymentData.status,
              method: paymentData.method,
              timestamp: new Date().toISOString(),
            }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log("✅ Subscription updated successfully:", result);

          // Store success info for redirect back to intelligence site
          localStorage.setItem(
            "fozzil-payment-success",
            JSON.stringify({
              payment_id: paymentData.payment_id,
              email: paymentData.email,
              plan: paymentData.plan,
              timestamp: Date.now(),
            })
          );

          return result;
        }
      } catch (apiError) {
        console.log(
          "⚠️ Direct API failed, trying alternative methods:",
          apiError.message
        );
      }

      // Method 2: Update via Supabase (if using same Supabase instance)
      try {
        const { supabase } = await import("../supabaseClient");

        // Update user's subscription in the database
        const { data, error } = await supabase
          .from("user_subscriptions")
          .upsert({
            user_id: paymentData.user_id,
            email: paymentData.email,
            payment_id: paymentData.payment_id,
            plan_type: paymentData.plan,
            status: paymentData.status,
            amount: paymentData.amount,
            payment_method: paymentData.method,
            created_at: new Date().toISOString(),
            expires_at: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(), // 30 days from now
          });

        if (!error) {
          console.log("✅ Subscription updated in Supabase:", data);
          return data;
        } else {
          console.error("❌ Supabase update failed:", error);
        }
      } catch (supabaseError) {
        console.log("⚠️ Supabase update failed:", supabaseError.message);
      }

      // Method 3: postMessage to parent window (if opened from intelligence.fozzil.in)
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "PAYMENT_SUCCESS",
            paymentData: paymentData,
          },
          "https://intelligence.fozzil.in"
        );
        console.log("✅ Sent payment success message to parent window");
      }

      // Method 4: Store in localStorage for intelligence site to pick up
      localStorage.setItem(
        "fozzil-subscription-update",
        JSON.stringify({
          ...paymentData,
          timestamp: Date.now(),
          processed: false,
        })
      );

      console.log("✅ Subscription update stored locally");
    } catch (error) {
      console.error("❌ Error updating subscription status:", error);
      throw error;
    }
  };

  // Simplified Razorpay Payment Integration (Subscription only)
  const handleRazorpayPayment = () => {
    console.log("� Redirecting to subscription flow...");
    handleRazorpaySubscription();
  };

  // Handle confirmation dialog responses
  const handleConfirmRedirect = () => {
    setShowConfirmDialog(false);
    if (pendingRedirectUrl) {
      window.location.href = pendingRedirectUrl;
    }
  };

  const handleCancelRedirect = () => {
    setShowConfirmDialog(false);
    setPendingRedirectUrl("");
  };

  // Function to redirect back to intelligence.fozzil.in with success status
  const redirectToIntelligence = (paymentSuccess = false) => {
    const baseUrl = "https://intelligence.fozzil.in";
    const params = new URLSearchParams({
      payment_completed: paymentSuccess,
      email: email,
      user_id: user?.id || "",
      timestamp: Date.now(),
    });

    if (paymentSuccess) {
      params.append("subscription_active", "true");
      params.append("plan", "Blacksite");
    }

    const redirectUrl = `${baseUrl}/dashboard?${params.toString()}`;
    console.log("🔄 Redirecting back to FOZZIL Intelligence:", redirectUrl);

    // Show confirmation dialog
    setPendingRedirectUrl(redirectUrl);
    setShowConfirmDialog(true);
  };

  // HDFC SmartGateway Payment Integration
  const handleHDFCPayment = async () => {
    console.log("🏦 Starting HDFC SmartGateway payment...");

    try {
      // Basic validation
      if (!email || email.trim() === "") {
        console.error("❌ Email validation failed for HDFC:", email);
        alert("Email address is required for HDFC payment processing");
        setLoading(false);
        return;
      }

      console.log("🏦 HDFC Payment details:", {
        email: email.trim(),
        amount: totalAmount,
        currency: "INR",
        userName: getNameFromEmail(email.trim()),
      });

      // Create HDFC payment request
      const hdfcPaymentData = {
        email: email.trim(),
        amount: totalAmount,
        currency: "INR",
        customerName: getNameFromEmail(email.trim()),
        phone: mobileNumber.trim() || "+919999999999", // Default or user phone
        productInfo:
          "FOZZIL Intelligence Blacksite Plan - Monthly Subscription",
        merchantId: "HDFC_MERCHANT_ID", // Will be replaced with actual merchant ID
        transactionId: `FOZZIL_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        successUrl: `${window.location.origin}/payment-success`,
        failureUrl: `${window.location.origin}/payment?error=hdfc_failed`,
        cancelUrl: `${window.location.origin}/payment?cancelled=true`,
      };

      console.log("🔄 Creating HDFC payment request...");

      // Send to backend for HDFC payment processing
      const response = await fetch(
        `${
          process.env.NODE_ENV === "production"
            ? "https://intelligence.fozzil.in"
            : "http://localhost:3001"
        }/api/hdfc/create-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(hdfcPaymentData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `HDFC API Error: ${errorData.error?.description || response.status}`
        );
      }

      const hdfcResult = await response.json();
      console.log("✅ HDFC payment request created:", hdfcResult);

      // Handle successful payment response and prepare for redirection
      if (hdfcResult.status === "success" && hdfcResult.transactionId) {
        await updateSubscriptionStatus({
          payment_id: hdfcResult.transactionId,
          email: email,
          user_id: user?.id,
          amount: totalAmount,
          plan: "Blacksite",
          status: "active",
          method: "hdfc_gateway",
        });
        redirectToIntelligence(true);
      }
      // Redirect to HDFC payment gateway
      else if (hdfcResult.paymentUrl) {
        console.log("🌐 Redirecting to HDFC SmartGateway...");
        window.location.href = hdfcResult.paymentUrl;
      } else {
        throw new Error("No payment URL received from HDFC gateway");
      }
    } catch (error) {
      console.error("❌ HDFC Payment error:", error);
      setLoading(false);

      // Show user-friendly error message
      alert(
        `HDFC Payment Error: ${error.message}\n\nPlease try Razorpay as an alternative payment method.`
      );
    }
  };

  const handlePayment = async () => {
    if (selectedPaymentMethod === "razorpay_subscription") {
      await handleRazorpaySubscription();
      return;
    } else if (selectedPaymentMethod === "hdfc") {
      handleHDFCPayment();
      setLoading(false);
    } else {
      setLoading(false);
      alert("Please select a valid payment method");
    }
  };

  // Show loading screen while fetching user data
  if (userLoading) {
    return (
      <div className="bg-white text-black min-h-screen py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-black min-h-[100dvh] safe-area-inset-bottom safe-area-inset-top">
      <div className="max-w-5xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row gap-6 md:gap-8 justify-center pt-safe pb-safe">
        {/* Left Side - Plan Details */}
        <div className="lg:w-5/12 lg:sticky lg:top-4 lg:self-start">
          <div className="mb-4 mt-safe">
            <button
              onClick={() => navigate("/pricing")}
              className="group relative transition-all duration-300 p-2 rounded-lg mb-2 flex items-center hover:bg-gray-50/50"
            >
              <svg
                className="w-5 h-5 text-gray-400 mr-2 transition-colors group-hover:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <div className="relative">
                <img
                  src="/logo.png"
                  alt="FOZZIL"
                  className="h-8 md:h-10 w-auto object-contain transition-opacity duration-300 group-hover:opacity-0"
                />
                <span className="absolute inset-0 flex items-center text-gray-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                  Back to Pricing
                </span>
              </div>
            </button>
            <h2 className="text-3xl font-bold mb-2">
              Subscribe to Blacksite Plan
            </h2>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold">
                ₹{planPrice.toFixed(2)}
              </span>
              <span className="text-gray-400 ml-2">per month</span>
            </div>
          </div>

          {/* Plan Details */}
          <div className="bg-white rounded-3xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-200 shadow-lg">
            <div className="flex justify-between items-start md:items-center gap-3 mb-4">
              <div>
                <h3 className="font-semibold text-black text-base md:text-lg">
                  Blacksite Plan Subscription
                </h3>
                <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                  Monthly billing
                </p>
              </div>
              <span className="font-semibold text-black whitespace-nowrap">
                ₹{planPrice.toFixed(2)}
              </span>
            </div>
            <hr className="border-gray-300 my-3 md:my-4" />{" "}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span>₹{planPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Convenience Fee</span>
                <span>₹{convenienceFee.toFixed(2)}</span>
              </div>

              <hr className="border-gray-300 my-2" />

              <div className="flex justify-between font-bold text-lg text-black">
                <span>Total due today</span>
                <span>₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Plan Features */}
          <div className="text-sm text-gray-700 hidden md:block">
            <h4 className="font-semibold text-black mb-2">Plan Features:</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <svg
                  className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>FI1.1 and FI2.4 Intelligence Engines</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Advanced Deep Think Mode</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Stealth Mode - leaves no trace</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Enhanced messaging and file handling</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Full creative suite</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Side - Payment Form */}
        <div className="lg:w-5/12">
          <div className="bg-white rounded-3xl p-4 md:p-6 border border-gray-200 shadow-lg mt-6">
            <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-black">
              Payment Information
            </h3>

            {/* Contact Information */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-black">
                Contact Information
              </h4>
              <div className="flex items-center gap-4">
                <label className="text-sm text-gray-600 w-16 flex-shrink-0">
                  Email<span className="text-red-500">*</span>
                </label>
                <div className="flex-1 relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      if (autoEntryStatus === "not_found") {
                        setEmail(e.target.value);
                      }
                    }}
                    readOnly={autoEntryStatus === "found"}
                    className={`w-full p-2 border ${
                      !email && "border-red-300"
                    } rounded-full text-black pr-20 ${
                      autoEntryStatus === "found"
                        ? "bg-gray-100 cursor-not-allowed"
                        : "bg-white focus:outline-none focus:border-blue-500"
                    }`}
                    placeholder={
                      email ? email : "Enter your FOZZIL Intelligence email"
                    }
                    required
                  />
                  {autoEntryStatus === "checking" && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span className="ml-1 text-xs text-blue-600">
                        Auto-detecting...
                      </span>
                    </div>
                  )}
                  {autoEntryStatus === "found" && email && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                      <svg
                        className="w-4 h-4 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      <span className="ml-1 text-xs text-green-600">
                        Auto-filled
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {autoEntryStatus === "not_found" && !email && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    Please enter the email address from your FOZZIL Intelligence
                    account
                  </div>
                </div>
              )}

              {!email && autoEntryStatus === "checking" && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                    Checking for your FOZZIL Intelligence account details...
                  </div>
                </div>
              )}

              {/* Mobile Number */}
              <div className="mt-4 flex items-center gap-4">
                <label className="text-sm text-gray-600 w-16 flex-shrink-0">
                  Mobile<span className="text-red-500">*</span>
                </label>
                <div className="flex-1 relative">
                  <div className="flex">
                    <div className="relative">
                      <select
                        className="h-full py-2 px-3 border rounded-l-full text-black bg-white focus:outline-none focus:border-blue-500"
                        value="+91"
                        disabled
                      >
                        <option value="+91">+91</option>
                      </select>
                    </div>
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Remove any non-numeric characters
                        const numericValue = value.replace(/\D/g, "");
                        // Limit to 10 digits for Indian phone numbers
                        if (numericValue.length <= 10) {
                          setMobileNumber(numericValue);
                          setValidationError("");
                        }
                      }}
                      className={`flex-1 p-2 border-y border-r ${
                        !mobileNumber && "border-red-300"
                      } rounded-r-full text-black bg-white focus:outline-none focus:border-blue-500`}
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                      pattern="[0-9]{10}"
                      required
                    />
                    {(!mobileNumber || validationError) && (
                      <div className="absolute -bottom-6 left-0 text-xs text-red-600">
                        {validationError || "Mobile number is required"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-black">Payment Method</h4>

              <div className="border rounded-3xl p-2 border-gray-300">
                {/* Razorpay Subscription Option */}
                <div
                  className={`p-2 cursor-pointer transition-colors rounded-2xl mb-2 ${
                    selectedPaymentMethod === "razorpay_subscription"
                      ? "bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() =>
                    setSelectedPaymentMethod("razorpay_subscription")
                  }
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="razorpay_subscription"
                      checked={
                        selectedPaymentMethod === "razorpay_subscription"
                      }
                      onChange={() =>
                        setSelectedPaymentMethod("razorpay_subscription")
                      }
                      className="mr-3"
                    />
                    <div className="flex items-center">
                      <img
                        src="https://razorpay.com/favicon.png"
                        alt="Razorpay"
                        className="w-4 h-4 mr-2"
                      />
                      <span className="font-medium text-black">Razorpay</span>
                    </div>
                  </div>
                </div>

                {/* HDFC SmartGateway Option */}
                <div
                  className={`p-2 cursor-pointer transition-colors rounded-2xl ${
                    selectedPaymentMethod === "hdfc"
                      ? "bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedPaymentMethod("hdfc")}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="hdfc"
                      checked={selectedPaymentMethod === "hdfc"}
                      onChange={() => setSelectedPaymentMethod("hdfc")}
                      className="mr-3"
                    />
                    <div className="flex items-center">
                      <img
                        src="/hdfc logo.png"
                        alt="HDFC"
                        className="w-4 h-4 mr-2"
                      />
                      <span className="font-medium text-black">
                        HDFC SmartGateway
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="mb-6 space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-1 mr-3 rounded-full"
                  required
                />
                <span className="text-sm text-gray-600">
                  You'll be charged the amount and at the frequency listed above
                  until you cancel. We may change our prices as described in our{" "}
                  <a
                    href="https://fozzil.in/terms-of-use"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>
                  . You can cancel any time. By subscribing, you agree to
                  FOZZIL's{" "}
                  <a
                    href="https://fozzil.in/terms-of-use"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://fozzil.in/privacy-policy"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  , and you authorize us to store your payment method for
                  renewals and other purchases.
                </span>
              </label>
            </div>

            {/* Subscribe Button */}
            <div className="sticky bottom-0 -mx-4 -mb-4 md:static md:mx-0 md:mb-0 px-4 py-4 bg-white border-t md:border-0 md:bg-transparent safe-area-inset-bottom">
              <button
                onClick={handlePayment}
                disabled={
                  loading ||
                  !email ||
                  !mobileNumber ||
                  !agreeTerms ||
                  validationError
                }
                className="w-full py-3.5 md:py-4 bg-gradient-to-r from-[#0099f2] to-[#1a6dff] text-white font-semibold rounded-full hover:shadow-lg active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing Payment...
                  </span>
                ) : (
                  "Subscribe Now"
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500">
              <p>
                Powered by <span className="text-gray-500">FOZZIL</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Payment Successful! 🎉
              </h3>
              <p className="text-gray-600">
                Would you like to return to FOZZIL Intelligence to start using
                your Blacksite features?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancelRedirect}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Stay Here
              </button>
              <button
                onClick={handleConfirmRedirect}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to FOZZIL Intelligence
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
